import React, { useEffect, useRef, useState } from 'react';
import {
  MIN_TERM_LENGTH,
  applySuggestion,
  extractCurrentTerm,
  fetchMedicalSuggestions,
  getAutocompleteDomain,
  isAutocompleteEnabled,
} from '../../services/medicalAutocomplete';

const DEBOUNCE_MS = 400;

// Drop-in replacement for the plain <textarea> on the medical list
// questions. Suggests correctly-spelled completions (via Perplexity Sonar)
// for the term currently being typed; falls back to a plain textarea for
// non-medical questions or when the feature flag is off.
const AutocompleteTextarea = ({
  question,
  value,
  onChange,
  rows = 4,
  className = 'form-input resize-none',
  required = false,
  dataTestId = 'textarea-field',
}) => {
  const domain = getAutocompleteDomain(question?.id);
  const enabled = Boolean(domain) && isAutocompleteEnabled();

  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const abortRef = useRef(null);
  const lastAcceptedRef = useRef('');

  useEffect(() => {
    if (!enabled) return undefined;

    const { term } = extractCurrentTerm(value);
    if (
      term.trim().length < MIN_TERM_LENGTH ||
      term.trim() === lastAcceptedRef.current
    ) {
      setSuggestions([]);
      setOpen(false);
      return undefined;
    }

    const timer = setTimeout(async () => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const results = await fetchMedicalSuggestions(
          domain,
          term,
          controller.signal
        );
        // Only show suggestions that still add something to what's typed.
        const fresh = results.filter(
          (s) => s.toLowerCase() !== term.trim().toLowerCase()
        );
        setSuggestions(fresh);
        setActiveIndex(-1);
        setOpen(fresh.length > 0);
      } catch {
        // Aborted by a newer keystroke — the next request owns the state.
      }
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [value, domain, enabled]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const accept = (suggestion) => {
    lastAcceptedRef.current = suggestion.trim();
    onChange(applySuggestion(value, suggestion));
    setSuggestions([]);
    setOpen(false);
    setActiveIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      accept(suggestions[activeIndex]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div className="relative">
      <textarea
        value={value}
        onChange={(e) => {
          lastAcceptedRef.current = '';
          onChange(e.target.value);
        }}
        onKeyDown={handleKeyDown}
        onBlur={() => setOpen(false)}
        placeholder={question?.placeholder || ''}
        rows={rows}
        className={className}
        data-testid={dataTestId}
        required={required}
        role={enabled ? 'combobox' : undefined}
        aria-expanded={enabled ? open : undefined}
        aria-autocomplete={enabled ? 'list' : undefined}
      />

      {open && (
        <ul
          className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden z-10"
          role="listbox"
          data-testid="autocomplete-suggestions"
        >
          {suggestions.map((suggestion, index) => (
            <li key={suggestion} role="option" aria-selected={index === activeIndex}>
              <button
                type="button"
                // onMouseDown so the click wins the race against textarea blur
                onMouseDown={(e) => {
                  e.preventDefault();
                  accept(suggestion);
                }}
                className={`w-full text-left px-4 py-2.5 text-sm ${
                  index === activeIndex
                    ? 'bg-[#e8f5ee] text-[#2d8a5f]'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
                data-testid={`suggestion-${index}`}
              >
                {suggestion}
              </button>
            </li>
          ))}
          <li className="px-4 py-1.5 text-[11px] text-gray-400 border-t border-gray-100">
            Suggestions may be incomplete — keep typing if yours isn't listed
          </li>
        </ul>
      )}
    </div>
  );
};

export default AutocompleteTextarea;
